import React, { useState } from "react";
import { useFormik } from "formik";
import { useQuery, useMutation, useQueryClient } from "react-query";
import Select from "react-select";
import { Button, Form, Table, Modal, Spinner, Row, Col, InputGroup } from "react-bootstrap";
import "./../../assets/scss/reports/cashbook.scss";
import ExpensesSelector from "../utils/ExpensesSelector";
import CurrencyCustomInput from "../utils/CurrencyCustomInput";
import { Popover } from "react-tiny-popover";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import { amountTypes } from "../../utils/helpers";
import DateTime from "react-datetime";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";

const cycleOptions = [
  { label: "", value: "" },
  { label: "Daily", value: "Daily" },
  { label: "Monthly", value: "Monthly" },
  { label: "Quaterly", value: "Quaterly" },
  { label: "Bi-annually", value: "Bi-anually" },
  { label: "Annually", value: "Annually" },
];

const usageOptions = [
  { value: "All Fixed Assets", label: "All Fixed Assets" },
  { value: "Category", label: "Category" },
  { value: "Items", label: "Items" },
];

export default function Depreciation() {
  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  //
  // API calls (replace with your real endpoints)
  const fetchCategories = async () => {
    const res = await fetch(`${backendUrl}/api/items/category`);
    return res.json();
  };
  const fetchItems = async () => {
    const res = await fetch(`${backendUrl}/api/items`);
    return res.json();
  };
  const fetchDepreciations = async () => {
    const res = await fetch(`${backendUrl}/api/depreciations`);
    return res.json();
  };
  const createDepreciation = async data => {
    const res = await fetch(`${backendUrl}/api/depreciations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return res.json();
  };
  const updateDepreciation = async ({ id, data }) => {
    const res = await fetch(`${backendUrl}/api/depreciations/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return res.json();
  };
  const deleteDepreciation = async id => {
    await fetch(`${backendUrl}/api/depreciations/${id}`, { method: "DELETE" });
  };

  // Queries
  // Fetch categories with safe destructuring
  const { data: categoriesData } = useQuery("categories", fetchCategories);
  const categories = categoriesData?.items || [];

  const { data: itemsData } = useQuery("items", fetchItems);
  const items = itemsData?.data?.items || [];

  const { data: depreciations = [], isLoading } = useQuery("depreciations", fetchDepreciations);

  // Options for selects
  const categoryOptions = categories?.map(cat => ({
    value: cat.id,
    label: cat.Cat_Name,
  }));
  const itemOptions = items?.map(item => ({
    value: item.Bar_Code,
    label: item.Item_Name,
  }));

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: editId
      ? depreciations.find(d => d.id === editId) || {
          cycle: "",
          type: "",
          fixedAmount: "",
          percentage: "",
          Post_Date: new Date().toISOString().slice(0, 10),
          Start_Date: new Date().toISOString().slice(0, 10),
          accumulatedAccountID: "",
          accumulatedAccountDescription: "",
          accumulatedAccountType: "",
          usage: "",
          usageItems: [],
        }
      : {
          cycle: "",
          type: "",
          fixedAmount: "",
          percentage: "",
          Post_Date: new Date().toISOString().slice(0, 10),
          Start_Date: new Date().toISOString().slice(0, 10),
          accumulatedAccountID: "",
          accumulatedAccountDescription: "",
          accumulatedAccountType: "",
          usage: "",
          usageItems: [],
        },
    onSubmit: (values, { resetForm }) => {
      if (editId) {
        updateMutation.mutate({ id: editId, data: values });
        setEditId(null);
      } else {
        createMutation.mutate(values);
      }
    },
  });

  // Handlers
  const handleEdit = id => {
    setEditId(id);
    setShowModal(true);
  };
  const handleDelete = id => {
    if (window.confirm("Delete this record?")) {
      deleteMutation.mutate(id);
    }
  };
  const handleShowModal = () => {
    setEditId(null);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setEditId(null);
    setShowModal(false);
    formik.resetForm();
  };

  // Mutations
  const createMutation = useMutation(createDepreciation, {
    onSuccess: () => {
      queryClient.invalidateQueries("depreciations");
      formik.resetForm();
      setShowModal(false);
    },
    onError: error => {
      toast.error?.(`Failed to create depreciation: ${error?.message}`);
    },
  });
  const updateMutation = useMutation(updateDepreciation, {
    onSuccess: () => {
      queryClient.invalidateQueries("depreciations");
      formik.resetForm();
      setShowModal(false);
    },
    onError: error => {
      toast?.error?.(`Failed to update depreciation: ${error?.message}`);
    },
  });
  const deleteMutation = useMutation(deleteDepreciation, {
    onSuccess: () => queryClient.invalidateQueries("depreciations"),
    onError: error => {
      toast?.error?.(`Failed to delete depreciation: ${error?.message}`);
    },
  });

  // GA ===============================================================
  const [showJournalPopover, setShowJournalPopover] = useState(false);
  const handleSelectedAccount = account => {
    // console.log(account);
    formik.setFieldValue("accumulatedAccountID", account?.AccountID);
    formik.setFieldValue("accumulatedAccountDescription", account?.Description);
    formik.setFieldValue("accumulatedAccountType", account?.Type);
    setShowJournalPopover(false);
  };

  const percentageValue = 0;

  return (
    <main className="inner-page-outlet">
      <main className="cash-book">
        <div className="content">
          {" "}
          <div className="content-main">
            <div className="content-body">
              {" "}
              <header>
                <h1>Depreciation Setup</h1>
                <Button variant="primary" onClick={handleShowModal} style={{ marginBottom: 24 }}>
                  Add Depreciation
                </Button>
              </header>
              <Modal show={showModal} onHide={handleCloseModal} size="md">
                <Modal.Header closeButton>
                  <Modal.Title>{editId ? "Edit Depreciation" : "Add Depreciation"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form onSubmit={formik.handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Usage</Form.Label>
                      <Select
                        options={usageOptions}
                        onChange={option => {
                          formik.setFieldValue("usage", option.value);
                          formik.setFieldValue("usageItems", []);
                        }}
                        value={usageOptions.find(o => o.value === formik.values.usage)}
                      />
                    </Form.Group>

                    {formik.values.usage === "Category" && (
                      <Form.Group className="mb-3">
                        <Form.Label>Categories</Form.Label>
                        <Select
                          isMulti
                          options={categoryOptions}
                          onChange={selected =>
                            formik.setFieldValue(
                              "usageItems",
                              selected ? selected.map(s => s.value) : []
                            )
                          }
                          value={categoryOptions.filter(o =>
                            formik.values.usageItems.includes(o.value)
                          )}
                        />
                      </Form.Group>
                    )}
                    {formik.values.usage === "Items" && (
                      <Form.Group className="mb-3">
                        <Form.Label>Items</Form.Label>
                        <Select
                          isMulti
                          options={itemOptions}
                          onChange={selected =>
                            formik.setFieldValue(
                              "usageItems",
                              selected ? selected.map(s => s.value) : []
                            )
                          }
                          value={itemOptions.filter(o =>
                            formik.values.usageItems.includes(o.value)
                          )}
                        />
                      </Form.Group>
                    )}
                    <hr />
                    <Row>
                      <Col>
                        <Form.Group className="mb-3">
                          <Form.Label>Cycle</Form.Label>
                          <Select
                            classNamePrefix={"form-select"}
                            options={cycleOptions}
                            value={cycleOptions.find(el => el.value === formik.values.cycle)}
                            onChange={selected => formik.setFieldValue("cycle", selected.value)}
                          />
                          {formik.touched.cycle && !!formik.errors.cycle ? (
                            <span className="text-danger mt-2">{formik.errors.cycle}</span>
                          ) : null}
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Type</Form.Label>
                          <Select
                            classNamePrefix={"form-select"}
                            options={amountTypes}
                            value={amountTypes?.find(el => el.value === formik.values?.type)}
                            onChange={selected => formik.setFieldValue("type", selected.value)}
                          />
                        </Form.Group>

                        {formik.values?.type === "Percentage" ? (
                          <Row>
                            <Col>
                              <Form.Group className="mb-3 pb-2">
                                <Form.Label className="mb-1">Percentage (%)</Form.Label>
                                <Form.Control
                                  type="number"
                                  name="percentage"
                                  value={formik.values.percentage}
                                  onChange={formik.handleChange}
                                />{" "}
                              </Form.Group>
                            </Col>
                            {/*  <Col>
                              <Form.Group className="mb-3 pb-2">
                                <Form.Label className="mb-1">Amount</Form.Label>
                                <CurrencyCustomInput
                                  //   name="amount"
                                  placeholder="0.00"
                                  value={percentageValue}
                                  onValueChange={(value, name) => {}}
                                  readOnly
                                />
                              </Form.Group>
                            </Col> */}
                          </Row>
                        ) : formik.values?.type === "Fixed" ? (
                          <Form.Group className="mb-3 pb-2">
                            <Form.Label className="mb-1">Fixed Amount</Form.Label>
                            <CurrencyCustomInput
                              name="fixedAmount"
                              placeholder="0.00"
                              value={formik.values.fixedAmount}
                              onValueChange={(value, name) => {
                                formik.setFieldValue(name, value);
                              }}
                            />{" "}
                          </Form.Group>
                        ) : null}

                        <Form.Group className="mb-3">
                          <Form.Label>Accumulated Depreciation Posting</Form.Label>
                          <div className="d-flex gap-3">
                            <InputGroup hasValidation>
                              <Form.Control
                                placeholder="Select Account"
                                value={`${formik.values?.accumulatedAccountID || "..."} / ${formik
                                  .values?.accumulatedAccountDescription || "..."} / ${formik.values
                                  ?.accumulatedAccountType || "..."}`}
                                onChange={() => {}}
                                readOnly
                                isInvalid={
                                  formik.touched.accumulatedAccountID &&
                                  !!formik.errors.accumulatedAccountID
                                }
                              />

                              <Popover
                                isOpen={showJournalPopover}
                                onClickOutside={() => setShowJournalPopover(false)}
                                content={() => (
                                  <ExpensesSelector
                                    handleSelectedExpense={handleSelectedAccount}
                                    usage={"chart of accounts"}
                                    //  onHide={() => setShowJournalPopover(false)}
                                    Type=""
                                  />
                                )}
                                position="bottom"
                              >
                                <InputGroup.Text onClick={() => setShowJournalPopover(true)}>
                                  <MagnifyIcon />
                                </InputGroup.Text>
                              </Popover>
                            </InputGroup>
                            {/*  <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => setShowCreateNewAccountModal(true)}
                              className="text-nowrap"
                            >
                              + Create New
                            </Button> */}
                          </div>
                        </Form.Group>

                        <hr />

                        <Form.Group className="mb-3">
                          <Form.Label>Start Date</Form.Label>
                          <DateTime
                            timeFormat={false}
                            closeOnSelect={true}
                            closeOnClickOutside={true}
                            dateFormat="MMM DD, YYYY"
                            inputProps={{
                              className: `date-input form-control`,
                              placeholder: "Select date",
                              readOnly: true,
                            }}
                            value={formik.values.Start_Date}
                            onChange={date => {
                              formik.setFieldValue("Start_Date", date, true);
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Post Date</Form.Label>
                          <DateTime
                            timeFormat={false}
                            closeOnSelect={true}
                            closeOnClickOutside={true}
                            dateFormat="MMM DD, YYYY"
                            inputProps={{
                              className: `date-input form-control`,
                              placeholder: "Select date",
                              readOnly: true,
                            }}
                            value={formik.values.Post_Date}
                            onChange={date => {
                              formik.setFieldValue("Post_Date", date, true);
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2 justify-content-end">
                      <Button variant="secondary" type="button" onClick={handleCloseModal}>
                        Cancel
                      </Button>

                      <Button
                        type="submit"
                        variant="primary"
                        disabled={createMutation.isLoading || updateMutation.isLoading}
                      >
                        {createMutation.isLoading || updateMutation.isLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : editId ? (
                          "Update"
                        ) : (
                          "Submit"
                        )}
                      </Button>
                    </div>
                  </Form>
                </Modal.Body>
              </Modal>
              <div className="px-4">
                {isLoading ? (
                  <div>
                    <Spinner animation="border" size="sm" /> Loading...
                  </div>
                ) : (
                  <Table responsive borderless striped className="product-table">
                    <thead>
                      <tr>
                        <th>Cycle</th>
                        <th>Type</th>
                        <th>Fixed Amount</th>
                        <th>Percentage</th>
                        <th>Post Date</th>
                        <th>Start Date</th>
                        <th>Accum. Account ID</th>
                        <th>Accum. Account Desc</th>
                        <th>Accum. Account Type</th>
                        <th>Usage</th>
                        <th>Usage Items</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {depreciations.map(d => (
                        <tr key={d.id}>
                          <td>{d.cycle}</td>
                          <td>{d.type}</td>
                          <td>{d.fixedAmount}</td>
                          <td>{d.percentage}</td>
                          <td>{d.Post_Date}</td>
                          <td>{d.Start_Date}</td>
                          <td>{d.accumulatedAccountID}</td>
                          <td>{d.accumulatedAccountDescription}</td>
                          <td>{d.accumulatedAccountType}</td>
                          <td>{d.usage}</td>
                          <td>
                            {(d.usage === "Category"
                              ? categoryOptions
                              : d.usage === "Items"
                              ? itemOptions
                              : []
                            )
                              .filter(o => d.usageItems?.includes(o.value))
                              .map(o => o.label)
                              .join(", ")}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEdit(d.id)}
                              className="me-2"
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(d.id)}
                              disabled={deleteMutation.isLoading}
                            >
                              {deleteMutation.isLoading ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                "Delete"
                              )}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {depreciations.length === 0 && (
                        <tr>
                          <td colSpan={12}>No records found.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>{" "}
          </div>{" "}
        </div>
      </main>
    </main>
  );
}
